import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { IStorageUnit } from '../storageUnit/storageUnit';
import { ArrayHelper, IOptionType } from '@zz2/zz2-ui';
import { IStorageUnitLocationUpsert } from '../storageUnitLocation/storageUnitLocationUpsert';
import { IStorageUnitFormValues } from './storageUnitFormValues';
import { IStorageUnitUpsert } from './storageUnitUpsert';
import { IStorageLocation } from '../storageLocation/storageLocation';
import { ISite } from '../site/site';

export default class StorageUnitModelHelper {
    public static createFormValues = (
        storageUnit : IStorageUnit | null,
        defaultSite : ISite | null,
        sites : Array<ISite>,
        storageLocations : Array<IStorageLocation>,
    ) : IStorageUnitFormValues => {
        if (!storageUnit) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            description: '',
            site: defaultSite // OptionType.FromDataModel
                ? { label: `(${defaultSite.shortDescription}) - ${defaultSite.description}`, value: defaultSite.id }
                : null,
            latitude: null,
            longitude: null,
            storageLocations: [],
            isTippable: false,
            hasRawStock: false,
            hasPackedStock: false,
            isActive: true,
        };

        const site = sites.find(x => x.id === storageUnit.siteId);

        const storageLocationIds = storageUnit.storageUnitLocations.filter(x => x.isActive).map(bridge => bridge.storageLocationId);
        const storageLocationOptions = storageLocations.filter(x => storageLocationIds.includes(x.id))
            .map((x) => {
                return { label: `(${x.code}) - ${x.description}`, value: x.id };
            });

        return {
            id: storageUnit.id,
            guid: storageUnit.guid,
            code: storageUnit.code,
            description: storageUnit.description,
            site: site // OptionType.FromDataModel
                ? { label: `(${site.shortDescription}) - ${site.description}`, value: site.id }
                : null,
            latitude: storageUnit.latitude,
            longitude: storageUnit.longitude,
            isTippable: storageUnit.isTippable,
            hasRawStock: storageUnit.hasRawStock,
            hasPackedStock: storageUnit.hasPackedStock,
            storageLocations: storageLocationOptions,
            isActive: storageUnit.isActive,
        };
    };

    public static createUpsert = (formValues : IStorageUnitFormValues, storageUnit : IStorageUnit | null) : IStorageUnitUpsert => {
        const existingBridges = storageUnit?.storageUnitLocations ?? [];

        const updatedStorageLocationIds = ArrayHelper
            .uniqueJoin(formValues.storageLocations.map(x => Number(x.value)), existingBridges.map(x => x.storageLocationId), a => a);

        const upsertBridges : Array<IStorageUnitLocationUpsert> = updatedStorageLocationIds.map((locationId) => {
            const shouldBeActiveBridge = formValues.storageLocations.find(x => Number(x.value) === locationId);
            const existingBridge = existingBridges.find(x => x.storageLocationId === locationId);

            return {
                id: existingBridge?.id ?? 0,
                guid: existingBridge?.guid ?? uuidV4(),
                storageLocationId: locationId,
                storageUnitId: storageUnit?.id ?? 0,
                isActive: !!shouldBeActiveBridge,
            };
        });

        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            description: formValues.description,
            siteId: Number(formValues.site?.value ?? 0),
            latitude: formValues.latitude,
            longitude: formValues.longitude,
            isTippable: formValues.isTippable,
            hasRawStock: formValues.hasRawStock,
            hasPackedStock: formValues.hasPackedStock,
            storageUnitLocationUpsertList: upsertBridges,
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<IStorageUnitFormValues> => Yup.object().shape({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        description: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        site: Yup.mixed<IOptionType>().defined().nullable().required('Required'),
        storageLocations: Yup.array().nullable().defined().required('Required'),
        latitude: Yup.number().typeError('Enter a number').defined().nullable(),
        longitude: Yup.number().typeError('Enter a number').defined().nullable(),
        isTippable: Yup.boolean().required('Required'),
        hasRawStock: Yup.boolean().required('Required'),
        hasPackedStock: Yup.boolean().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });
}
